import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Slider,
  Stack,
  useTheme as useMuiTheme,
  Button,
} from '@mui/material';
import { Brightness4, Brightness7, GridView, Email } from '@mui/icons-material';
import { useTheme } from './ThemeProvider.tsx';

interface GalleryAppBarProps {
  columns: number;
  textSize: number;
  onColumnsChange: (value: number) => void;
  onTextSizeChange: (value: number) => void;
}

export const GalleryAppBar: React.FC<GalleryAppBarProps> = ({
  columns,
  textSize,
  onColumnsChange,
  onTextSizeChange,
}) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-form');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        backgroundColor: muiTheme.palette.background.paper,
        borderBottom: `1px solid ${muiTheme.palette.divider}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '80px !important' }}>
        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GridView sx={{ color: muiTheme.palette.primary.main }} />
          <Typography 
            variant="h6" 
            component="div" 
            fontFamily="Playfair Display"
            color="text.primary"
          >
            Gallery Controls
          </Typography>
        </Box>

        {/* Gallery Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: '350px' }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: '150px' }}>
            <Typography 
              variant="body2" 
              fontFamily="Bitter"
              color="text.secondary"
              sx={{ minWidth: '60px' }}
            >
              Columns: {columns}
            </Typography>
            <Slider
              value={columns}
              onChange={(_, value) => onColumnsChange(value as number)}
              min={1}
              max={8}
              step={1}
              size="small"
              sx={{
                width: 100,
                color: muiTheme.palette.primary.main,
              }}
            />
          </Stack>


          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: '150px' }}>
            <Typography
              variant="body2"
              fontFamily="Bitter"
              color="text.secondary"
              sx={{ minWidth: '60px' }}
            >
              Text Size: {textSize.toFixed(1)}x
            </Typography>
            <Slider
              value={textSize}
              onChange={(_, value) => onTextSizeChange(value as number)}
              min={0.5}
              max={2.0}
              step={0.1}
              size="small"
              sx={{
                width: 100,
                color: muiTheme.palette.primary.main,
              }}
            />
          </Stack>
        </Box>

        {/* Contact and Theme Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={scrollToContact}
            startIcon={<Email />}
            sx={{
              color: muiTheme.palette.text.primary,
              fontFamily: 'Bitter',
              textTransform: 'none',
              backgroundColor: muiTheme.palette.action.hover,
              '&:hover': {
                backgroundColor: muiTheme.palette.action.selected,
              },
            }}
          >
            Contact
          </Button>
          
          <IconButton
            onClick={toggleTheme}
            color="primary"
            sx={{
              backgroundColor: muiTheme.palette.action.hover,
              '&:hover': {
                backgroundColor: muiTheme.palette.action.selected,
              },
            }}
          >
            {isDarkMode ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};